// SMG Sonic Identity — a bespoke ~1.4s brand audio signature.
// Web Audio API only — no MP3 assets, no CDN, no license issues.
// Plays once per browser session unless silenced.

let ctxRef = null;
let didPlay = false;

function getCtx() {
  if (ctxRef) return ctxRef;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  ctxRef = new Ctx();
  return ctxRef;
}

/**
 * Plays the SMG sonic identity — a 3-note ident with a rising synth pad
 * and a soft cinematic swell. Total duration ~1.4s.
 * Silent if the browser blocks autoplay or audio isn't available.
 */
export function playSonicId({ force = false } = {}) {
  if (didPlay && !force) return;
  const ctx = getCtx();
  if (!ctx) return;

  const now = ctx.currentTime;
  const master = ctx.createGain();
  master.gain.value = 0;
  master.gain.linearRampToValueAtTime(0.22, now + 0.15);
  master.gain.linearRampToValueAtTime(0.0, now + 1.4);
  master.connect(ctx.destination);

  // Base rising pad — layered sines for warmth
  const notes = [
    { f: 196.0, t: 0.0 }, // G3
    { f: 261.63, t: 0.18 }, // C4
    { f: 392.0, t: 0.42 }, // G4 — the "SMG" resolve
  ];

  notes.forEach(({ f, t }) => {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(f, now + t);
    osc.frequency.exponentialRampToValueAtTime(f * 1.005, now + t + 0.6);
    g.gain.value = 0;
    g.gain.linearRampToValueAtTime(0.9, now + t + 0.06);
    g.gain.exponentialRampToValueAtTime(0.0001, now + t + 0.8);
    osc.connect(g).connect(master);
    osc.start(now + t);
    osc.stop(now + t + 0.9);

    // Add a subtle detuned overtone for cinematic body
    const overtone = ctx.createOscillator();
    const og = ctx.createGain();
    overtone.type = 'triangle';
    overtone.frequency.setValueAtTime(f * 2, now + t);
    og.gain.value = 0;
    og.gain.linearRampToValueAtTime(0.15, now + t + 0.06);
    og.gain.exponentialRampToValueAtTime(0.0001, now + t + 0.6);
    overtone.connect(og).connect(master);
    overtone.start(now + t);
    overtone.stop(now + t + 0.7);
  });

  // Cinematic "swell" — a low sub whoosh
  const sub = ctx.createOscillator();
  const subG = ctx.createGain();
  sub.type = 'sine';
  sub.frequency.setValueAtTime(65, now);
  sub.frequency.exponentialRampToValueAtTime(48, now + 1.2);
  subG.gain.value = 0;
  subG.gain.linearRampToValueAtTime(0.25, now + 0.35);
  subG.gain.exponentialRampToValueAtTime(0.0001, now + 1.3);
  sub.connect(subG).connect(master);
  sub.start(now);
  sub.stop(now + 1.35);

  // High shimmer — a sparse noise burst
  const noiseBuf = ctx.createBuffer(1, ctx.sampleRate * 0.3, ctx.sampleRate);
  const noiseData = noiseBuf.getChannelData(0);
  for (let i = 0; i < noiseData.length; i++) {
    noiseData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / noiseData.length, 2);
  }
  const noise = ctx.createBufferSource();
  const nG = ctx.createGain();
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = 6000;
  bp.Q.value = 4;
  nG.gain.value = 0.08;
  noise.buffer = noiseBuf;
  noise.connect(bp).connect(nG).connect(master);
  noise.start(now + 0.42);
  noise.stop(now + 0.72);

  didPlay = true;
}

// Arm the sonic identity to fire on the first genuine user interaction —
// required because browsers block autoplay of unmuted audio otherwise.
export function armSonicIdOnFirstInteraction() {
  if (didPlay) return;
  const fire = () => {
    playSonicId();
    window.removeEventListener('pointerdown', fire);
    window.removeEventListener('keydown', fire);
    window.removeEventListener('touchstart', fire);
  };
  window.addEventListener('pointerdown', fire, { once: true });
  window.addEventListener('keydown', fire, { once: true });
  window.addEventListener('touchstart', fire, { once: true });
}
